/**
 * AuthContext — Reactive authentication state for the entire app.
 *
 * Wraps the app and provides:
 *   - Current user / session (reactive — updates on auth state changes)
 *   - User profile from user_profiles table
 *   - Loading state during initial session check
 *   - isAuthenticated boolean for route guards
 *
 * Uses Supabase onAuthStateChange listener for real-time session updates.
 * Components consume via useAuthContext() — never access supabase.auth directly.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';
import type { UserProfile } from '@/types/database';
import type { Session, User } from '@supabase/supabase-js';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface AuthState {
  /** Supabase auth user */
  readonly user: User | null;
  /** Supabase session */
  readonly session: Session | null;
  /** App-level user profile from user_profiles table */
  readonly profile: UserProfile | null;
  /** True while checking initial session */
  readonly loading: boolean;
  /** Convenience boolean */
  readonly isAuthenticated: boolean;
  /** Force refresh profile from database */
  readonly refreshProfile: () => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthState | null>(null);

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

interface AuthProviderProps {
  readonly children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  /** Fetch user profile from user_profiles table */
  const fetchProfile = useCallback(async (userId: string): Promise<void> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Failed to fetch user profile:', error.message);
        setProfile(null);
        return;
      }

      setProfile(data as UserProfile);
    } catch {
      console.error('Profile fetch failed');
      setProfile(null);
    }
  }, []);

  /** Public refresh for downstream use */
  const refreshProfile = useCallback(async (): Promise<void> => {
    if (user) {
      await fetchProfile(user.id);
    }
  }, [user, fetchProfile]);

  // ─── Initial session check ───────────────────────────────────────────

  useEffect(() => {
    let mounted = true;

    const initSession = async (): Promise<void> => {
      try {
        const { data: { session: existingSession } } = await supabase.auth.getSession();

        if (!mounted) return;

        if (existingSession?.user) {
          setUser(existingSession.user);
          setSession(existingSession);
          await fetchProfile(existingSession.user.id);
        }
      } catch {
        console.error('Initial session check failed');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void initSession();

    return () => {
      mounted = false;
    };
  }, [fetchProfile]);

  // ─── Auth state change listener ──────────────────────────────────────

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (event === 'SIGNED_IN' && newSession?.user) {
          await fetchProfile(newSession.user.id);
        }

        if (event === 'SIGNED_OUT') {
          setProfile(null);
        }

        if (event === 'TOKEN_REFRESHED' && newSession?.user) {
          // Session refreshed — profile stays the same
        }

        if (event === 'USER_UPDATED' && newSession?.user) {
          await fetchProfile(newSession.user.id);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // ─── Context value ───────────────────────────────────────────────────

  const value: AuthState = {
    user,
    session,
    profile,
    loading,
    isAuthenticated: user !== null && session !== null,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useAuthContext(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within <AuthProvider>');
  }
  return context;
}
